import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Search, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

interface Entry { id: string; to: string; subject: string; kind: string; ref: string; name: string; status: "sent" | "failed"; provider_id?: string; error?: string; created_at: string }

const KIND: Record<string, string> = {
  paid: "Confirmación de pago", confirmed: "Reserva confirmada", contact: "Solicitud recibida", balance: "Aviso de saldo", balance_paid: "Saldo recibido",
  refund: "Cancelación / reembolso", admin_paid: "Aviso interno (pago)", admin_contact: "Aviso interno (solicitud)",
};
const kindLabel = (k: string) => KIND[k] || (k.startsWith("test_") ? `Prueba · ${KIND[k.slice(5)] || k.slice(5)}` : k);

export default function EmailLog() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const { data = [], isLoading } = useQuery<Entry[]>({ queryKey: ["email-log", q, status], queryFn: async () => (await api.get("/admin/emails", { params: { q, status } })).data });
  return (
    <div data-testid="email-log-page">
      <h1 className="font-display text-3xl sm:text-4xl text-[#0B192C]">Envíos de correo</h1>
      <p className="mt-1 text-sm text-slate-500">Historial de correos enviados a clientes y avisos internos. "Enviado" significa aceptado por el proveedor de correo.</p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por correo, folio, nombre o asunto…" data-testid="email-log-search" className="pl-9" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="email-log-status" className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">Todos</option><option value="sent">Enviados</option><option value="failed">Fallidos</option>
        </select>
        <span className="text-xs text-slate-500" data-testid="email-log-count">{data.length} correos</span>
      </div>
      <div className="mt-4 space-y-2">
        {!isLoading && data.length === 0 && <p className="text-sm text-slate-500" data-testid="email-log-empty">No hay envíos registrados.</p>}
        {data.map((e) => (
          <div key={e.id} data-testid={`email-log-row-${e.id}`} className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-start gap-3">
            {e.status === "sent" ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-[200px]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-[#0B192C] text-sm">{e.to}</span>
                <span className={`text-[11px] rounded-full px-2 py-0.5 ${e.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid={`email-log-status-${e.id}`}>{e.status === "sent" ? "Enviado" : "Falló"}</span>
                <span className="text-xs text-slate-500">{kindLabel(e.kind)}</span>
              </div>
              <p className="mt-1 text-xs text-slate-600">{e.subject}</p>
              <p className="mt-1 text-xs text-slate-400">{[e.name, e.ref && `Folio ${e.ref.slice(0, 8).toUpperCase()}`].filter(Boolean).join(" · ")} · {new Date(e.created_at).toLocaleString("es-MX")}</p>
              {e.error && <p className="mt-1 text-xs text-red-600" data-testid={`email-log-error-${e.id}`}>{e.error}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
